import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setError("Order not found."));
  }, [orderId]);

  if (error) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-32 text-center">
        <SEO title="Order" />
        <p className="font-serif text-4xl">{error}</p>
        <Link to="/" className="mt-6 inline-block font-mono-cap text-xs underline">Back to home →</Link>
      </section>
    );
  }
  if (!order) return <p className="text-center py-32 text-white/50">Loading order...</p>;

  return (
    <>
      <SEO title="Order Confirmed" />
      <section className="max-w-3xl mx-auto px-5 py-16">
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="text-center">
          <CheckCircle2 size={56} className="mx-auto text-emerald-400" data-testid="order-success-icon" />
          <h1 className="font-serif text-4xl sm:text-5xl mt-4">Order Confirmed.</h1>
          <p className="text-white/60 mt-2">We sent the details to our team and they'll reach out to you shortly.</p>
          {order.payment_method === "UPI" && (
            <p className="mt-4 mx-auto max-w-md border border-[#FF69B4]/40 bg-[#FF69B4]/10 text-white/80 text-sm px-4 py-3" data-testid="upi-pending-note">
              Your order is confirmed pending payment verification. We will dispatch within 24 hours of payment confirmation.
            </p>
          )}
        </motion.div>

        <div className="border border-white/10 mt-10 p-6 bg-[#0a0a0a]">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <div>
              <p className="font-mono-cap text-[10px] text-white/40">Order Number</p>
              <p className="text-2xl font-serif mt-1" data-testid="order-number">{order.order_number}</p>
            </div>
            <div className="sm:text-right">
              <p className="font-mono-cap text-[10px] text-white/40">Payment</p>
              <p className="text-lg mt-1">{order.payment_method}</p>
              {order.utr && <p className="text-xs text-white/50 mt-1">UTR: <span className="font-mono text-white/80" data-testid="order-utr">{order.utr}</span></p>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-6 pt-6 border-t border-white/10">
            <div>
              <p className="font-mono-cap text-[10px] text-white/40 mb-2">Delivering to</p>
              <p className="text-sm">{order.full_name}</p>
              <p className="text-sm text-white/70">{order.address}, {order.city} - {order.pincode}</p>
              <p className="text-sm text-white/70">{order.phone}</p>
              <p className="text-sm text-white/70">{order.email}</p>
            </div>
            <div>
              <p className="font-mono-cap text-[10px] text-white/40 mb-2">Items</p>
              {order.items.map((i, idx) => (
                <p key={idx} className="text-sm">{i.product_name} · {i.size} × {i.quantity}</p>
              ))}
              <p className="text-sm font-medium mt-3 pt-3 border-t border-white/10">Total: ₹{order.total}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <a href="https://wa.me/918383862206" target="_blank" rel="noreferrer" className="border border-white/15 p-4 flex items-center gap-3 hover:bg-white/5" data-testid="whatsapp-confirm">
            <MessageCircle size={18} /> <span className="text-sm">WhatsApp us for updates</span>
          </a>
          <a href="mailto:thedripchexk@gmail.com" className="border border-white/15 p-4 flex items-center gap-3 hover:bg-white/5">
            <Mail size={18} /> <span className="text-sm">thedripchexk@gmail.com</span>
          </a>
        </div>

        <div className="text-center mt-12 space-y-4">
          <Link to={`/review?order=${order.order_number}`} data-testid="leave-review-link" className="block font-mono-cap text-xs text-[#FF69B4] underline underline-offset-4">Loved it? Leave a review →</Link>
          <Link to="/collections" data-testid="continue-shopping" className="block font-mono-cap text-xs underline underline-offset-4">Continue shopping →</Link>
        </div>
      </section>
    </>
  );
};

export default OrderConfirmation;
